import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { CardTransaction } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge/resources/bridgeApi/types';
import { Button } from 'components/app-components/ui/button';
import { ScrollArea } from 'components/app-components/ui/scroll-area';
import { Skeleton } from 'components/app-components/ui/skeleton';
import { Tooltip } from 'components/app-components/ui/tooltip';
import { TransactionItem } from './TransactionItem';

interface TransactionsListProps {
  transactions: CardTransaction[] | null;
  isFetching: boolean;
  isPinUpdating: boolean;
  isManagePinDisabled?: boolean;
  managePinDisabledReason?: string;
  hideCardActions?: boolean;
  onManagePINClick: () => void;
  /** Card available balance, already formatted (e.g. "$10.00"). */
  cardBalanceDisplay?: string;
  /** Click handler for the "Add Funds" button. */
  onAddFundsClick: () => void;
  /** Disable Add Funds when wallet has no balance or required ids are missing. */
  isAddFundsDisabled: boolean;
  /** Tooltip / explanation when Add Funds is disabled. */
  addFundsDisabledReason?: string;
}

// Skeleton loading component for transactions
const TransactionSkeleton = () => {
  return [...Array(3)].map((_, index) => (
    <div key={index} className="flex items-center justify-between gap-3 py-3">
      <div className="flex flex-col items-start gap-1">
        <Skeleton className="h-4 w-24 bg-neutral-700" />
        <Skeleton className="h-3 w-16 bg-neutral-700" />
      </div>
      <Skeleton className="h-4 w-16 bg-neutral-700" />
    </div>
  ));
};

/**
 * Transactions list display with balance and optional card actions.
 */
export const TransactionsList = ({
  transactions,
  isFetching,
  isPinUpdating,
  isManagePinDisabled,
  managePinDisabledReason,
  hideCardActions,
  onManagePINClick,
  cardBalanceDisplay,
  onAddFundsClick,
  isAddFundsDisabled,
  addFundsDisabledReason,
}: TransactionsListProps) => {
  return (
    <div className="flex w-full min-w-0 flex-col md:flex-[2_2_0%]">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <p className="text-xs font-medium text-neutral-500">Available balance</p>
          <p className="mt-1 truncate text-3xl font-bold text-white">{cardBalanceDisplay ?? '—'}</p>
        </div>
        {!hideCardActions && (
          <div className="flex flex-col gap-1">
            <Button
              variant="primary"
              size="sm"
              onClick={onAddFundsClick}
              disabled={isAddFundsDisabled}
              tooltipContent={isAddFundsDisabled ? addFundsDisabledReason : null}
            >
              <span className="text-xs font-medium">Top Balance</span>
            </Button>
            <Button
              variant="plain"
              size="sm"
              onClick={onManagePINClick}
              disabled={isPinUpdating || isManagePinDisabled}
              loading={isPinUpdating}
              tooltipContent={isManagePinDisabled ? managePinDisabledReason : null}
            >
              <span className="text-xs font-medium text-neutral-300 underline">Manage PIN</span>
            </Button>
          </div>
        )}
      </div>
      <div className="mb-2 flex items-center gap-1">
        <h4 className="text-xs font-medium text-neutral-500">Transactions</h4>
        <Tooltip
          content="Transfers are instant in most cases (within 10-15 minutes) and can’t be reversed."
          className="max-w-xs"
        >
          <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-neutral-500 hover:text-neutral-300" />
        </Tooltip>
      </div>
      {/* Cap the list to ~4 rows and scroll the rest so the panel stays compact. The max height
          must sit on the Radix viewport, not the root: the root only clips and never scrolls. */}
      <ScrollArea className="[&>[data-slot=scroll-area-viewport]]:max-h-48 [&_[data-slot=scroll-area-thumb]]:bg-neutral-600">
        <div className="flex flex-col divide-y divide-neutral-700">
          {isFetching ? (
            <TransactionSkeleton />
          ) : transactions?.length > 0 ? (
            transactions.map((transaction) => <TransactionItem key={transaction.id} transaction={transaction} />)
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-neutral-600 px-4 py-3">
              <p className="text-sm font-medium text-neutral-500">No transactions found</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
